/**
 * useHasPermission — does the signed-in user hold `<action>:<resource>`?
 *
 * It ANSWERS FROM WHAT THE SERVER SENT. `GET /users/me` returns the caller's
 * resolved grant set — `resolvePrincipal`'s own `permissions[]`, the same
 * object `requirePermission` asks on every request — and this hook looks the
 * key up in it. There is no computation here and none anywhere else in
 * packages/web.
 *
 * GUARDED BY A LINT. `scripts/ci/no-web-derivation-lint.mjs` fails the build
 * when packages/web starts computing a permission answer again — a role
 * ladder, a rank array, a role-literal compare used as authority, one of the
 * identifiers B1-T3 deleted, or a Drizzle schema import. It runs in the CI
 * `guards` job (.github/workflows/ci.yml, "Foundations-1 lints — no web
 * derivation") with its own detector tests beside it.
 *
 * Until review SEC-B1b-09 this comment denied the guard: written while B1-T5
 * was still unbuilt, it survived the batch that built it, so the first file a
 * reviewer opens to check the browser is not deriving authority told them
 * nothing was watching — while CI had been failing the build on exactly that
 * since the commit before. A reviewer who believes no lint is watching
 * re-reviews by hand what CI already covers; one who believes in a lint that
 * is not there reviews nothing. Both are the same bug, so what this file says
 * about that script is now itself checked by a test —
 * `__tests__/lint-claims-match-the-tree.test.ts`.
 *
 * ─── What it used to do (Foundations 1 / B1-T3) ───────────────────────────
 *
 *     if (!isRoleId(user.role)) return false;
 *     return hasPermission(user.role, action, resource);
 *
 * `hasPermission` indexed `ROLE_PERMISSIONS`, a copy of the server's grant
 * matrix compiled into the browser bundle and keyed by one of five role
 * strings. Two consequences, both live:
 *
 *   - A tenant's OWN role was not a `RoleId`, so `isRoleId` returned false and
 *     the hook answered NO to everything — every affordance hidden from a user
 *     the gateway would have allowed.
 *   - A shipped bundle that gained a grant in a release stayed stale in every
 *     browser until the bundle was rebuilt and shipped.
 *
 * Use this for affordances (hide / disable buttons), never as the security
 * barrier. The API enforces RBAC at the route layer; this only decides what to
 * OFFER.
 *
 * @example
 * const canEditUsers = useHasPermission('write', 'users');
 * {canEditUsers && <Button>Edit user</Button>}
 *
 * @example
 * const canResetMfa = useHasPermission('mfa:reset', 'users');
 * <Button disabled={!canResetMfa}>Reset MFA</Button>
 */
import {
  permissionKey,
  type RbacAction,
  type RbacResource,
} from '@argusai/shared/schemas/permissions';
import { useAuth } from '@/contexts/auth-context';

/**
 * `true` iff the server said this user holds the grant. `false` for an
 * unauthenticated shell, for a bootstrap that could not read `/users/me`, and
 * for any key not in the set — nothing fails open (principles.md #4).
 */
export function useHasPermission(
  action: RbacAction,
  resource: RbacResource
): boolean {
  const { can } = useAuth();
  return can(permissionKey(action, resource));
}
